import { API } from "@/config/apiBase";
import { getStoredTheme, setTheme } from "@/config/theme";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, ArrowLeft, User, Mail, Bell, Shield, LogOut, Trash2, Save,
  Moon, Sun, Clock, Download, Gift, Globe, CreditCard, ExternalLink, Upload,
  MessageSquarePlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";
import FamilySettings from "@/components/FamilySettings";
import GoogleContactsImport from "@/components/GoogleContactsImport";
import FeedbackModal from "@/components/FeedbackModal";
import { trackUpgradeClicked, trackCancelClicked } from "@/utils/analytics";


const Settings = () => {
  const { user, setUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Compute effective plan for UI display and feature gating
  // Rule: Use effective_plan if available (handles family member inheritance)
  const planForUI = user?.effective_plan ?? user?.subscription_plan ?? "free";
  const isFamilyMember = user?.family_role === "member" && user?.family_group_id;
  
  // Debug logging for plan resolution
  console.log("[Settings] planForUI", planForUI, "sub", user?.subscription_plan, "effective", user?.effective_plan, "family_role", user?.family_role);
  
  // Initialize dark_mode from stored theme
  const [settings, setSettings] = useState({
    email_reminders: true,
    in_app_notifications: true,
    weekly_digest: false,
    reminder_hour: 9,
    dark_mode: getStoredTheme() === 'dark',
    timezone: "America/New_York"
  });
  
  // Auto-gift state removed - feature coming soon
  
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isSyncingBilling, setIsSyncingBilling] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Auto-refresh subscription state after returning from Stripe Portal
  useEffect(() => {
    const billingReturn = searchParams.get('billing');
    const tab = searchParams.get('tab');
    
    if (billingReturn === 'return' || tab === 'billing') {
      console.log("[Settings] Detected return from Stripe Portal, starting subscription sync...");
      setIsSyncingBilling(true);
      
      // Store initial subscription state for comparison
      const initialState = {
        plan: user?.subscription_plan,
        cancel: user?.subscription_cancel_at_period_end,
        period_end: user?.subscription_current_period_end
      };
      
      let pollCount = 0;
      const maxPolls = 15; // 30 seconds max (15 polls * 2 seconds)
      
      const pollSubscription = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            const newState = {
              plan: response.data.subscription_plan,
              cancel: response.data.subscription_cancel_at_period_end,
              period_end: response.data.subscription_current_period_end
            };
            
            // Check if state changed
            const hasChanged = 
              newState.plan !== initialState.plan ||
              newState.cancel !== initialState.cancel ||
              newState.period_end !== initialState.period_end;
            
            setUser(response.data);
            pollCount++;
            
            console.log(`[Settings] Poll ${pollCount}/${maxPolls} - changed: ${hasChanged}`, newState);
            
            if (hasChanged || pollCount >= maxPolls) {
              // Success - state updated or timeout
              setIsSyncingBilling(false);
              if (hasChanged) {
                toast.success("Billing updated successfully");
              }
              // Clean up URL params
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('billing');
              setSearchParams(newParams, { replace: true });
              return;
            }
            
            // Continue polling
            setTimeout(pollSubscription, 2000);
          }
        } catch (error) {
          console.error("[Settings] Poll failed:", error);
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(pollSubscription, 2000);
          } else {
            setIsSyncingBilling(false);
          }
        }
      };
      
      // Start polling immediately
      pollSubscription();
    }
  }, []); // Only run once on mount

  // Refresh on window focus (handles portal opening in same/new tab)
  useEffect(() => {
    const handleFocus = async () => {
      console.log("[Settings] Window focused, refreshing user data...");
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error("[Settings] Focus refresh failed:", error);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [setUser]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      // Fetch user settings and auto-gift settings SEPARATELY
      const userSettingsResponse = await axios.get(`${API}/settings/user`);
      console.log("[Settings] Loaded user settings:", userSettingsResponse.data);
      // Auto-gift settings fetch removed - feature coming soon
      
      // Merge user settings with defaults (ONLY from /settings/user)
      const loadedUserSettings = {
        email_reminders: userSettingsResponse.data.email_reminders ?? true,
        in_app_notifications: userSettingsResponse.data.in_app_notifications ?? true,
        weekly_digest: userSettingsResponse.data.weekly_digest ?? false,
        reminder_hour: userSettingsResponse.data.reminder_hour ?? 9,
        dark_mode: userSettingsResponse.data.dark_mode ?? false,
        timezone: userSettingsResponse.data.timezone ?? "America/New_York"
      };
      
      // Set state
      setSettings(loadedUserSettings);
      
      // Sync dark mode with theme system
      if (loadedUserSettings.dark_mode !== (getStoredTheme() === 'dark')) {
        setTheme(loadedUserSettings.dark_mode ? 'dark' : 'light');
      }
      
      console.log("[Settings] Final user settings state:", loadedUserSettings);
    } catch (error) {
      console.error("[Settings] load failed", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    console.log("[Settings] Saving user settings:", settings);
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      console.log("[Settings] Auth token present:", !!token);
      
      // Save user settings and auto-gift settings SEPARATELY
      const userResponse = await axios.put(`${API}/settings/user`, settings);
      console.log("[Settings] User settings saved:", userResponse.status, userResponse.data);
      
      // Auto-gift settings save removed - feature coming soon
      
      toast.success("Settings saved!");
      
      // Sync dark mode with theme system after save
      setTheme(settings.dark_mode ? 'dark' : 'light');
      
    } catch (error) {
      console.error("[Settings] Save error:", error.response?.status, error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/export/all-contacts?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'outtatouch_contacts.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Contacts exported!");
      } else {
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'outtatouch_contacts.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Contacts exported!");
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore logout API errors - proceed with client-side cleanup
    }
    
    // Use context logout - clears state and redirects immediately
    toast.success("Logged out successfully");
    logout();
  };

  const handleManageBilling = async () => {
    // Track cancel/manage billing click
    trackCancelClicked("settings_billing");
    
    setIsBillingLoading(true);
    try {
      const response = await axios.post(`${API}/billing/portal`);
      const { url } = response.data;
      
      if (url) {
        // Redirect to Stripe Customer Portal in same tab
        window.location.href = url;
      } else {
        toast.error("Could not open billing portal");
      }
    } catch (error) {
      console.error("[Settings] Billing portal error:", error);
      
      if (error.response?.status === 401) {
        toast.error("Please log in to manage billing");
        navigate("/login");
      } else if (error.response?.status === 400) {
        // Specific message for no billing account
        const detail = error.response?.data?.detail || "";
        if (detail.includes("No billing account") || detail.includes("subscribe")) {
          toast.error("No subscription found. Please upgrade first.");
        } else {
          toast.error(detail || "Could not access billing portal");
        }
      } else if (error.response?.status === 404) {
        toast.error("No subscription found. Please upgrade first.");
      } else {
        toast.error("Could not open billing portal. Please try again.");
      }
    } finally {
      setIsBillingLoading(false);
    }
  };

  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
    { value: "Europe/London", label: "London (GMT)" },
    { value: "Europe/Paris", label: "Paris (CET)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
    { value: "Australia/Sydney", label: "Sydney (AEST)" }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5 text-[#3D405B]" />
                </Button>
              </Link>
              <h1 className="text-xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                Settings
              </h1>
            </div>
            <Button 
              onClick={saveSettings}
              disabled={isSaving}
              className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
              data-testid="save-settings"
            >
              {isSaving ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save</>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Profile Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Profile
            </h2>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-[#E07A5F] to-[#F2CC8F] rounded-2xl flex items-center justify-center text-white text-3xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#3D405B]">{user?.name}</h3>
              <p className="text-[#6D6F7C]">{user?.email}</p>
              <p className="text-sm text-[#81B29A] mt-1 capitalize">
                {planForUI} Plan
                {isFamilyMember && planForUI === "family" && (
                  <span className="text-[#6D6F7C] font-normal"> (via family)</span>
                )}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Billing Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          id="billing-section"
          data-testid="billing-section"
        >
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Billing
            </h2>
            {isSyncingBilling && (
              <span className="text-xs text-[#6D6F7C] flex items-center gap-1">
                <div className="animate-spin w-3 h-3 border-2 border-[#E07A5F] border-t-transparent rounded-full"></div>
                Syncing...
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3D405B]">Current Plan</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-[#81B29A] capitalize">
                    {planForUI} Plan
                  </p>
                  {/* Comped/Admin-granted badge */}
                  {user?.is_comped && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Comped
                    </span>
                  )}
                </div>
                {/* Family member helper text */}
                {isFamilyMember && planForUI === "family" && (
                  <p className="text-xs text-[#81B29A] mt-1">
                    Managed by family owner. Your access comes from your family group.
                  </p>
                )}
                {/* Show comped helper text (only if not family member) */}
                {user?.is_comped && !isFamilyMember && (
                  <p className="text-xs text-purple-600 mt-1">
                    Admin granted. No Stripe billing is active.
                  </p>
                )}
                {/* Show "Cancel anytime" for paid Stripe plans (not comped, not family member) */}
                {planForUI !== "free" && !user?.subscription_cancel_at_period_end && !user?.is_comped && !isFamilyMember && (
                  <p className="text-xs text-[#6D6F7C] mt-1">
                    Cancel anytime. If you cancel, you'll keep access until your billing date.
                  </p>
                )}
              </div>
              {/* Hide upgrade button for family members - they're covered by owner */}
              {!(isFamilyMember && planForUI === "family") && (
                <Link to="/pricing" onClick={() => trackUpgradeClicked(planForUI === "free" ? "plus" : "change", "settings")}>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-[#F4F1EA] rounded-xl"
                    data-testid="upgrade-plan-button"
                  >
                    {planForUI === "free" ? "Upgrade" : "Change Plan"}
                  </Button>
                </Link>
              )}
            </div>

            {planForUI !== "free" && !isFamilyMember && (
              <>
                {/* Show cancellation notice if applicable */}
                {/* Priority: if cancel_at_period_end=true, use current_period_end; else use cancel_at */}
                {user?.subscription_cancel_at_period_end && (user?.subscription_current_period_end || user?.subscription_cancel_at) && (
                  <>
                    <Separator className="bg-[#F4F1EA]" />
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="font-medium text-amber-800">Subscription Ending</p>
                      <p className="text-sm text-amber-700">
                        Your {planForUI} plan will end on{' '}
                        <span className="font-semibold">
                          {new Date((user.subscription_current_period_end || user.subscription_cancel_at) * 1000).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        You'll keep access until then. Reactivate anytime via Manage Billing.
                      </p>
                    </div>
                  </>
                )}
                
                {/* Show renewal date if not cancelling AND period_end exists */}
                {!user?.subscription_cancel_at_period_end && user?.subscription_current_period_end && (
                  <>
                    <Separator className="bg-[#F4F1EA]" />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#3D405B]">Next Renewal Date</p>
                        <p className="text-sm text-[#6D6F7C]">
                          {new Date(user.subscription_current_period_end * 1000).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                <Separator className="bg-[#F4F1EA]" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#3D405B]">Manage Subscription</p>
                    <p className="text-sm text-[#6D6F7C]">Update payment method, view invoices, or cancel</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleManageBilling}
                      disabled={isBillingLoading}
                      className="bg-[#3D405B] hover:bg-[#2D303B] text-white rounded-xl"
                      data-testid="manage-billing-button"
                    >
                      {isBillingLoading ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <>
                          Manage Billing
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Family Plan Section - Only show if user is in a family group */}
        {(user?.family_group_id || user?.family_role) && (
          <FamilySettings />
        )}

        {/* Notifications Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Bell className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Notifications
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between opacity-50">
              <div>
                <p className="font-medium text-[#3D405B]">Email Reminders</p>
                <p className="text-sm text-[#6D6F7C]">Coming soon</p>
              </div>
              <Switch
                checked={false}
                disabled
                data-testid="email-reminders-switch"
              />
            </div>
            
            <Separator className="bg-[#F4F1EA]" />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3D405B]">In-App Notifications</p>
                <p className="text-sm text-[#6D6F7C]">Show notifications within the app</p>
              </div>
              <Switch
                checked={settings.in_app_notifications}
                onCheckedChange={(checked) => setSettings({ ...settings, in_app_notifications: checked })}
                data-testid="in-app-notifications-switch"
              />
            </div>
            
            <Separator className="bg-[#F4F1EA]" />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3D405B]">Weekly Digest</p>
                <p className="text-sm text-[#6D6F7C]">Receive a weekly summary of your connections</p>
              </div>
              <Switch
                checked={settings.weekly_digest}
                onCheckedChange={(checked) => setSettings({ ...settings, weekly_digest: checked })}
                data-testid="weekly-digest-switch"
              />
            </div>
            
            <Separator className="bg-[#F4F1EA]" />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3D405B]">Reminder Time</p>
                <p className="text-sm text-[#6D6F7C]">When to send daily reminders</p>
              </div>
              <Select 
                value={String(settings.reminder_hour)} 
                onValueChange={(v) => setSettings({ ...settings, reminder_hour: parseInt(v) })}
              >
                <SelectTrigger className="w-36 bg-white border-[#F4F1EA] rounded-xl" data-testid="reminder-time-select">
                  <Clock className="w-4 h-4 mr-2 text-[#6D6F7C]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl max-h-64">
                  {Array.from({ length: 24 }, (_, i) => i).map(hour => {
                    // Format: 12:00 AM, 1:00 AM, ..., 12:00 PM, 1:00 PM, ..., 11:00 PM
                    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                    const amPm = hour < 12 ? 'AM' : 'PM';
                    return (
                      <SelectItem key={hour} value={String(hour)} data-testid={`reminder-hour-${hour}`}>
                        {displayHour}:00 {amPm}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Auto-Gift Section - Coming Soon */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Automated Birthday Gifts
            </h2>
            <span className="text-xs bg-[#F2CC8F]/30 text-[#E07A5F] px-2 py-0.5 rounded-full font-medium">
              Coming Soon
            </span>
          </div>

          <div className="bg-[#F4F1EA]/50 rounded-xl p-4">
            <p className="text-sm text-[#6D6F7C]">
              Automatically send thoughtful gifts on birthdays and special occasions. 
              We&apos;re working on this feature and will notify you when it&apos;s ready.
            </p>
          </div>
        </motion.div>

        {/* Preferences Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Preferences
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#3D405B] dark:text-white">Dark Mode</p>
                <p className="text-sm text-[#6D6F7C] dark:text-gray-400">Switch to dark theme</p>
              </div>
              <Switch
                checked={settings.dark_mode}
                onCheckedChange={(checked) => {
                  setSettings({ ...settings, dark_mode: checked });
                  setTheme(checked ? 'dark' : 'light');
                }}
                data-testid="dark-mode-switch"
              />
            </div>
            
            <Separator className="bg-[#F4F1EA]" />
            
            <div>
              <Label className="text-[#3D405B]">Timezone</Label>
              <Select 
                value={settings.timezone} 
                onValueChange={(v) => setSettings({ ...settings, timezone: v })}
              >
                <SelectTrigger className="w-full bg-white border-[#F4F1EA] rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                  {timezones.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Export Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Download className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Export Data
            </h2>
          </div>

          <p className="text-sm text-[#6D6F7C] mb-4">
            Download your contacts and call history
          </p>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={isExporting}
              className="flex-1 border-[#F4F1EA] rounded-xl"
              data-testid="export-csv"
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('json')}
              disabled={isExporting}
              className="flex-1 border-[#F4F1EA] rounded-xl"
              data-testid="export-json"
            >
              Export JSON
            </Button>
          </div>
        </motion.div>

        {/* Import Section */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Upload className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Import Contacts
            </h2>
          </div>

          <p className="text-sm text-[#6D6F7C] mb-4">
            Import contacts from Google to quickly add people you want to stay in touch with.
          </p>

          <GoogleContactsImport 
            onImportComplete={(result) => {
              toast.success(`Imported ${result.imported_count} contacts. Skipped ${result.skipped_count} duplicates.`);
            }}
          />
        </motion.div>

        {/* Help & Feedback Section */}
        <motion.div 
          className="mt-6 bg-white rounded-3xl p-6 border border-[#F4F1EA] shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <MessageSquarePlus className="w-5 h-5 text-[#81B29A]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Help & Feedback
            </h2>
          </div>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start border-[#F4F1EA] rounded-xl h-12 text-[#3D405B]"
              onClick={() => setShowFeedbackModal(true)}
              data-testid="send-feedback-button"
            >
              <MessageSquarePlus className="w-4 h-4 mr-3 text-[#81B29A]" />
              Send feedback
            </Button>
            <p className="text-xs text-[#6D6F7C] pl-1">
              Found a bug? Have a feature idea? We'd love to hear from you.
            </p>
          </div>
        </motion.div>

        {/* Account Actions */}
        <motion.div 
          className="card-contact"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-[#E07A5F]" />
            <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Account
            </h2>
          </div>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start border-[#F4F1EA] rounded-xl h-12 text-[#3D405B]"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
            
            <Button
              variant="ghost"
              className="w-full justify-start text-[#E07A5F] hover:bg-[#E07A5F]/10 rounded-xl h-12"
              onClick={() => toast.info("Contact support to delete your account")}
              data-testid="delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-3" />
              Delete Account
            </Button>
          </div>
        </motion.div>

        {/* App Info */}
        <div className="text-center mt-8 text-[#9CA3AF] text-sm">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-6 h-6 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-3 h-3 text-white" />
            </div>
            <span className="font-medium">OuttaTouch</span>
          </div>
          <p>Version 1.0.0</p>
          <p className="mt-1">Made with ❤️ for meaningful connections</p>
        </div>
      </main>

      {/* Feedback Modal */}
      <FeedbackModal 
        open={showFeedbackModal} 
        onOpenChange={setShowFeedbackModal} 
      />
    </div>
  );
};

export default Settings;
